// src/pages/supplier/ProfilePage.jsx
// Phase 1 — M1: Supplier profile + KYC submission + scorecard tab

import { useState, useRef } from 'react'
import { useAuth } from '../../context/AuthContext'
import { supplierProfileApi, supplierKycApi } from '../../api/services'

const KYC_STATUS = {
  not_started: { label: 'Not Submitted',  color: 'text-gray-500',  bg: 'bg-gray-100'   },
  pending:     { label: 'Under Review',   color: 'text-yellow-700', bg: 'bg-yellow-100' },
  under_review:{ label: 'Being Reviewed', color: 'text-blue-700',   bg: 'bg-blue-100'   },
  verified:    { label: 'Verified ✅',    color: 'text-green-700',  bg: 'bg-green-100'  },
  rejected:    { label: 'Rejected',       color: 'text-red-700',    bg: 'bg-red-100'    },
}

export default function SupplierProfilePage() {
  const { user, setUser } = useAuth()
  const [tab, setTab] = useState('profile')
  const [form, setForm] = useState({
    name:               user?.name || '',
    email:              user?.email || '',
    phone:              user?.phone || '',
    business_name:      user?.business_name || '',
    gstin:              user?.gstin || '',
    pan:                user?.pan || '',
    bank_account_number:user?.bank_account_number || '',
    bank_ifsc:          user?.bank_ifsc || '',
    bank_name:          user?.bank_name || '',
  })
  const [saving, setSaving]       = useState(false)
  const [saved, setSaved]         = useState(false)
  const [docType, setDocType]     = useState('gst_certificate')
  const [docFile, setDocFile]     = useState(null)
  const [uploading, setUploading] = useState(false)
  const [kycLoading, setKycLoading] = useState(false)
  const [scorecard, setScorecard] = useState(null)
  const [scorecardLoading, setScoreLoading] = useState(false)
  const fileRef = useRef()

  const kycStatus = user?.supplier_kyc_status || 'not_started'
  const kycInfo   = KYC_STATUS[kycStatus] || KYC_STATUS.not_started

  function set(key) { return e => setForm(f => ({ ...f, [key]: e.target.value })) }

  async function saveProfile(e) {
    e.preventDefault()
    setSaving(true)
    try {
      const res = await supplierProfileApi.update(form)
      setUser(res.data.data)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (err) {
      alert(err.response?.data?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function uploadDoc() {
    if (!docFile) return alert('Select a file first')
    setUploading(true)
    const fd = new FormData()
    fd.append('document', docFile)
    fd.append('type', docType)
    try {
      await supplierProfileApi.uploadDocument(fd)
      setDocFile(null)
      if (fileRef.current) fileRef.current.value = ''
      alert('Document uploaded. It will be reviewed within 2 business days.')
    } catch (err) {
      alert(err.response?.data?.message || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  async function submitKyc() {
    if (!window.confirm('Submit your profile for KYC verification?')) return
    setKycLoading(true)
    try {
      await supplierKycApi.submitForReview()
      alert('KYC submitted! Our team will review within 2–3 business days.')
      window.location.reload()
    } catch (err) {
      alert(err.response?.data?.message || 'Submission failed')
    } finally {
      setKycLoading(false)
    }
  }

  async function loadScorecard() {
    setScoreLoading(true)
    try {
      const res = await supplierKycApi.getScorecard()
      setScorecard(res.data.data)
    } catch {
      setScorecard(null)
    } finally {
      setScoreLoading(false)
    }
  }

  const inputClass = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">My Profile</h1>
      <p className="text-gray-500 mb-6">Manage your account, documents, and KYC status</p>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6 w-fit">
        {[['profile','Profile'], ['kyc','KYC & Documents'], ['scorecard','Scorecard']].map(([key, label]) => (
          <button key={key} onClick={() => { setTab(key); if (key === 'scorecard' && !scorecard) loadScorecard() }}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all
              ${tab === key ? 'bg-white text-[#FF3D7F] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Profile tab ── */}
      {tab === 'profile' && (
        <form onSubmit={saveProfile} className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4 shadow-sm">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-xs text-gray-500 mb-1 block">Full Name *</label>
              <input className={inputClass} value={form.name} onChange={set('name')} required /></div>
            <div><label className="text-xs text-gray-500 mb-1 block">Email *</label>
              <input className={inputClass} type="email" value={form.email} onChange={set('email')} required /></div>
            <div><label className="text-xs text-gray-500 mb-1 block">Phone</label>
              <input className={inputClass} value={form.phone} onChange={set('phone')} /></div>
            <div><label className="text-xs text-gray-500 mb-1 block">Business Name *</label>
              <input className={inputClass} value={form.business_name} onChange={set('business_name')} required /></div>
            <div><label className="text-xs text-gray-500 mb-1 block">GSTIN (15 chars)</label>
              <input className={inputClass} value={form.gstin} onChange={set('gstin')} maxLength={15} placeholder="22AAAAA0000A1Z5" /></div>
            <div><label className="text-xs text-gray-500 mb-1 block">PAN (10 chars)</label>
              <input className={inputClass} value={form.pan} onChange={set('pan')} maxLength={10} placeholder="AAAAA0000A" /></div>
          </div>

          <hr className="border-gray-100" />
          <p className="text-sm font-semibold text-gray-700">Bank Details (for payouts)</p>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-xs text-gray-500 mb-1 block">Account Number</label>
              <input className={inputClass} value={form.bank_account_number} onChange={set('bank_account_number')} /></div>
            <div><label className="text-xs text-gray-500 mb-1 block">IFSC Code</label>
              <input className={inputClass} value={form.bank_ifsc} onChange={set('bank_ifsc')} maxLength={11} /></div>
            <div className="col-span-2"><label className="text-xs text-gray-500 mb-1 block">Bank Name</label>
              <input className={inputClass} value={form.bank_name} onChange={set('bank_name')} /></div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button type="submit" disabled={saving}
              className="px-6 py-2.5 rounded-xl bg-[#FF3D7F] text-white font-semibold text-sm hover:bg-pink-600 disabled:opacity-50 transition-colors">
              {saving ? 'Saving…' : 'Save Profile'}
            </button>
            {saved && <span className="text-green-600 text-sm font-medium">✅ Saved!</span>}
          </div>
        </form>
      )}

      {/* ── KYC tab ── */}
      {tab === 'kyc' && (
        <div className="space-y-5">
          {/* KYC status card */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">KYC Status</h2>
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${kycInfo.bg} ${kycInfo.color}`}>
                {kycInfo.label}
              </span>
            </div>

            {kycStatus === 'not_started' && (
              <div className="bg-yellow-50 rounded-xl p-4 mb-4">
                <p className="text-sm text-yellow-800 font-medium mb-1">KYC verification required</p>
                <p className="text-xs text-yellow-700">Upload your GST certificate and PAN card, then submit for review to get the verified badge on your storefront.</p>
              </div>
            )}
            {kycStatus === 'verified' && (
              <div className="bg-green-50 rounded-xl p-4 mb-4">
                <p className="text-sm text-green-800 font-medium">Your account is fully verified ✅</p>
                <p className="text-xs text-green-700">You have the KYC Verified badge on your storefront.</p>
              </div>
            )}
            {kycStatus === 'rejected' && (
              <div className="bg-red-50 rounded-xl p-4 mb-4">
                <p className="text-sm text-red-800 font-medium">KYC rejected — please re-upload documents</p>
                <p className="text-xs text-red-700">Check your notifications for the specific reason and re-submit.</p>
              </div>
            )}

            {['not_started', 'rejected'].includes(kycStatus) && (
              <button onClick={submitKyc} disabled={kycLoading}
                className="px-5 py-2.5 rounded-xl bg-[#FF3D7F] text-white text-sm font-semibold hover:bg-pink-600 disabled:opacity-50 transition-colors">
                {kycLoading ? 'Submitting…' : '📋 Submit for KYC Review'}
              </button>
            )}
          </div>

          {/* Document upload */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
            <h2 className="font-semibold text-gray-900 mb-4">Upload Documents</h2>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Document Type</label>
                <select value={docType} onChange={e => setDocType(e.target.value)} className={inputClass}>
                  <option value="gst_certificate">GST Certificate</option>
                  <option value="pan_card">PAN Card</option>
                  <option value="cancelled_cheque">Cancelled Cheque</option>
                  <option value="incorporation_certificate">Certificate of Incorporation</option>
                  <option value="trade_licence">Trade Licence</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">File (JPG, PNG, PDF · max 5MB)</label>
                <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.pdf"
                  onChange={e => setDocFile(e.target.files[0])}
                  className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-pink-50 file:text-[#FF3D7F] hover:file:bg-pink-100" />
              </div>
              <button onClick={uploadDoc} disabled={uploading || !docFile}
                className="px-5 py-2.5 rounded-xl bg-[#FF8500] text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-50 transition-colors">
                {uploading ? 'Uploading…' : '⬆️ Upload Document'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Scorecard tab ── */}
      {tab === 'scorecard' && (
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <h2 className="font-semibold text-gray-900 mb-4">Your Performance Scorecard</h2>
          {scorecardLoading ? (
            <p className="text-gray-400 text-center py-8">Loading scorecard…</p>
          ) : scorecard ? (
            <div className="space-y-4">
              {/* Tier badge */}
              <div className="flex items-center gap-3">
                <span className={`px-4 py-1.5 rounded-full text-sm font-bold ${
                  scorecard.tier === 'platinum' ? 'bg-purple-100 text-purple-700' :
                  scorecard.tier === 'gold'     ? 'bg-yellow-100 text-yellow-700' :
                  scorecard.tier === 'silver'   ? 'bg-gray-100 text-gray-600' :
                  'bg-gray-50 text-gray-400'}`}>
                  {scorecard.tier?.toUpperCase()} SELLER
                </span>
                {scorecard.badges?.map(b => (
                  <span key={b.key} className="text-sm">{b.icon} {b.label}</span>
                ))}
              </div>

              {/* Metrics grid */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['On-Time Dispatch', scorecard.on_time_rate != null ? `${scorecard.on_time_rate}%` : '—'],
                  ['Avg Rating', scorecard.avg_rating != null ? `${scorecard.avg_rating} ★` : '—'],
                  ['Fulfillment Rate', scorecard.fulfillment_rate != null ? `${scorecard.fulfillment_rate}%` : '—'],
                  ['Repeat Buyer Rate', scorecard.repeat_buyer_rate != null ? `${scorecard.repeat_buyer_rate}%` : '—'],
                  ['Total Orders', scorecard.total_orders || 0],
                  ['Total Reviews', scorecard.total_reviews || 0],
                ].map(([label, value]) => (
                  <div key={label} className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1">{label}</p>
                    <p className="text-lg font-bold text-gray-900">{value}</p>
                  </div>
                ))}
              </div>

              {scorecard.scorecard_as_of && (
                <p className="text-xs text-gray-400">Last computed: {scorecard.scorecard_as_of}</p>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-400 mb-3">Scorecard not yet computed</p>
              <p className="text-xs text-gray-400">Scorecards are computed nightly after your first delivered order.</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
